import {
  DeactivationReason,
  Gender,
  MynevaPaymentMethod,
  OrderPaymentStatus,
} from './../../core/types/enums';
import { handleGender } from '../../core/services/utils/helpers';
import { JsonProperty } from '../../core/services/utils/mapping.service';
import { CorePartnerResidentAccountsDto } from './partner-sync.model';
import {
  PartnerName,
  getPartnerName,
} from '../../core/partners/partner-registry';

export interface ResidentPayload {
  customerId: number;
  department: string;
  departmentId: number;
  fname: string;
  gender: string;
  kostenstelle: string;
  lname: string;
  roomNumber: string;
  active: number;
}

export interface UpdateResident extends ResidentPayload {
  id: number;
}

interface GetNamePayload {
  withRoomNumber?: boolean;
  withGender?: boolean;
}

export class Resident {
  @JsonProperty('active')
  active: number = 1;
  @JsonProperty()
  deactivationReason: DeactivationReason | string = '';
  @JsonProperty('id')
  id: number = 0;
  @JsonProperty()
  roomNumber: string = '';
  @JsonProperty('gender')
  gender: string = '';
  @JsonProperty('fname')
  fname: string = '';
  @JsonProperty()
  favouriteProducts: number[] = [];
  @JsonProperty('lname')
  lname: string = '';
  @JsonProperty('kostenstelle')
  kostenstelle?: string = '';
  @JsonProperty('hasIban')
  hasIBAN?: number = 0;
  @JsonProperty()
  sepaEmail?: string = '';
  @JsonProperty()
  departmentId: number = 0;
  @JsonProperty()
  bankInfoId?: number = 0;
  @JsonProperty()
  residentNote?: string = '';
  @JsonProperty()
  residentId?: number = 0;
  @JsonProperty()
  partnerId?: number = 0;
  @JsonProperty()
  invoiceTotal?: number = 0;
  @JsonProperty()
  customerId: number = 0;
  @JsonProperty()
  editableInShop: number = 1;
  @JsonProperty('image')
  image: string = '';
  @JsonProperty()
  individualPaymentStatus: OrderPaymentStatus = OrderPaymentStatus.open;
  @JsonProperty()
  residentInvoiceNo: string = '';
  @JsonProperty()
  partnerResidentId: string = '';
  @JsonProperty()
  partnerAccountId: number = 0;
  @JsonProperty('color')
  color: string = '';
  @JsonProperty()
  partnerLastSynchronization?: string = '';
  @JsonProperty()
  mynevaPaymentMethod?: MynevaPaymentMethod = MynevaPaymentMethod.Lastschrift;
  @JsonProperty({ clazz: CorePartnerResidentAccountsDto, name: 'accounts' })
  accounts: CorePartnerResidentAccountsDto[] = [];
  @JsonProperty()
  monthlyBudgetResetType: string = '';
  @JsonProperty()
  monthlyBudgetValue: number = 0;
  @JsonProperty()
  paymentMethod: string = '';
  @JsonProperty('customerNotesInShop')
  internalNote: string = '';

  constructor(init?: Partial<Resident>) {
    Object.assign(this, init);
  }

  /** The partner this record belongs to, or undefined if it has none. */
  get partnerName(): PartnerName | undefined {
    return getPartnerName(this.partnerId);
  }

  get isMyNeva(): boolean {
    return this.partnerName === PartnerName.Myneva;
  }

  mapResidentToSave(hasId: boolean = false) {
    const residentPayload = {
      active: this.active,
      color: this.color,
      customerId: this.customerId,
      departmentId: this.departmentId,
      kostenstelle: this.kostenstelle,
      lname: this.lname,
      roomNumber: this.roomNumber,
      fname: this.fname,
      monthlyBudgetValue: this.monthlyBudgetValue,
      monthlyBudgetResetType: this.monthlyBudgetResetType,
      gender: this.gender,
      customerNotesInShop: this.internalNote,
      sepaEmail: this.sepaEmail,
    };
    if (hasId) {
      return {
        ...residentPayload,
        id: this.id,
        accountId: this.partnerAccountId,
      };
    }
    return residentPayload;
  }

  canOrderAsMyNeva?() {
    return (
      this.mynevaPaymentMethod === MynevaPaymentMethod.Verwahrgeld ||
      (this.mynevaPaymentMethod === MynevaPaymentMethod.Lastschrift &&
        this.hasIBAN == 1)
    );
  }

  getName({
    withGender = false,
    withRoomNumber = false,
  }: GetNamePayload = {}): string {
    let name = `${this.fname} ${this.lname}`;

    if (withGender) {
      name = `${handleGender(this.gender as Gender)} ${name}`;
    }
    if (withRoomNumber && this.roomNumber) {
      name = `${this.roomNumber} - ${name}`;
    }

    return name;
  }
}
